'use client';

import { FunctionComponent, useState, useEffect, useCallback } from 'react';
import { useRouter, usePathname, useParams } from 'next/navigation';
import config from '@config';
import { useTranslate } from '@xFrame4/components/Hooks';
import { useAppActionDispatch, useAppStateSelector } from '@redux/hooks';
import { setAppLanguage } from '@redux/layoutSlice';
import { UrlHelper, UrlType } from 'frontend_business/seo/UrlHelper';

export interface LanguageSelectorProps
{

}

/**
 * Component: language selector for the website
 */
const LanguageSelector: FunctionComponent<LanguageSelectorProps> = (props) =>
{
    const t = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const dispatch = useAppActionDispatch();
    const appLanguage = useAppStateSelector(state => state.layout.language);
    const router = useRouter();
    const pathname = usePathname();
    const params = useParams();

    /** Get current language from URL or Redux state */
    const getCurrentLanguage = useCallback(() => 
    {
        // Try to get language from URL params first
        if (params?.language && typeof params.language === 'string') 
        {
            const urlLanguage = config.languages.find(lang => lang.code === params.language);
            if (urlLanguage) 
            {
                return urlLanguage;
            }
        }

        // Fallback to Redux state or default language
        return appLanguage || config.defaultLanguage;
    }, [params?.language, appLanguage]);

    /** Initialize language from URL */
    useEffect(() => 
    {
        const currentLang = getCurrentLanguage();
        if (!appLanguage || appLanguage.code !== currentLang.code) 
        {
            dispatch(setAppLanguage(currentLang));
        }
    }, [getCurrentLanguage, appLanguage, dispatch]);

    /** Toggle dropdown */
    const toggleDropdown = () => 
    {
        setIsOpen(!isOpen);
    }

    /** Close dropdown */
    const closeDropdown = () => 
    {
        setIsOpen(false);
    }

    /** Handle language change */
    const handleLanguageChange = async (languageCode: string) => 
    {
        const selectedLanguage = config.languages.find(lang => lang.code === languageCode);
        if (selectedLanguage) 
        {
            // Update Redux state
            dispatch(setAppLanguage(selectedLanguage));
            
            // Navigate to the new language URL with proper slug translation
            const currentPath = pathname || '/';
            const pathSegments = currentPath.split('/').filter(segment => segment !== '');
            
            // Check if first segment is a language code
            const isLanguageInPath = config.languages.some(lang => lang.code === pathSegments[0]);
            
            let newPath: string;
            if (isLanguageInPath && pathSegments.length > 1) 
            {
                // Get current language and slug
                const currentLanguageCode = pathSegments[0];
                const currentSlug = pathSegments.slice(1).join('/');
                
                // Try to determine what URL type this slug represents
                const urlType = UrlHelper.getUrlTypeForLanguageAndSlug(currentLanguageCode, currentSlug);
                
                if (urlType !== null) 
                {
                    // We found a matching URL type, so we can translate the slug
                    const urlHelper = UrlHelper.instance;
                    
                    // Get the appropriate URL for the new language
                    switch (urlType) 
                    {
                        case UrlType.Rooms:
                            newPath = urlHelper.getRoomsUrl(languageCode);
                            break;
                        case UrlType.SpecialOffers:
                            newPath = urlHelper.getSpecialOffersUrl(languageCode);
                            break;
                        case UrlType.Restaurant:
                            newPath = urlHelper.getRestaurantUrl(languageCode);
                            break;
                        case UrlType.Events:
                            newPath = urlHelper.getEventsUrl(languageCode);
                            break;
                        case UrlType.Weddings:
                            newPath = urlHelper.getWeddingsUrl(languageCode);
                            break;
                        case UrlType.Spa:
                            newPath = urlHelper.getSpaUrl(languageCode);
                            break;
                        case UrlType.Contact:
                            newPath = urlHelper.getContactUrl(languageCode);
                            break;
                        case UrlType.PrivacyPolicy:
                            newPath = urlHelper.getPrivacyPolicyUrl(languageCode);
                            break;
                        case UrlType.TermsAndConditions:
                            newPath = urlHelper.getTermsAndConditionsUrl(languageCode);
                            break;
                        case UrlType.SpaTermsAndConditions:
                            newPath = urlHelper.getSpaTermsAndConditionsUrl(languageCode);
                            break;
                        case UrlType.CookiePolicy:
                            newPath = urlHelper.getCookiePolicyUrl(languageCode);
                            break;
                        case UrlType.RoomType:
                        case UrlType.SpecialOffer:
                        case UrlType.RestaurantBox:
                        case UrlType.EventRoom:
                        case UrlType.SpaInfoBox:
                            // For detail pages, we need to translate the slug based on the entity
                            // This will be handled by a new method in UrlHelper
                            newPath = await urlHelper.getTranslatedUrlForDetailPage(urlType, currentLanguageCode, languageCode, pathSegments);
                            break;
                        default:
                            // Fallback to just changing the language code
                            pathSegments[0] = languageCode;
                            newPath = '/' + pathSegments.join('/');
                            break;
                    }
                } 
                else 
                {
                    // URL type not found, just replace the language segment
                    pathSegments[0] = languageCode;
                    newPath = '/' + pathSegments.join('/');
                }
            } 
            else if (isLanguageInPath && pathSegments.length === 1) 
            {
                // We're on a language-only URL (like /ro), go to home page of new language
                newPath = '/' + languageCode;
            }
            else if (currentPath === '/' || currentPath === '') 
            {
                // Root path - just add language
                newPath = '/' + languageCode;
            }
            else 
            {
                // Add language segment at the beginning for other paths
                newPath = '/' + languageCode + currentPath;
            }
            
            router.push(newPath);
        }
        closeDropdown();
    }

    /** Get current language */
    const currentLanguage = getCurrentLanguage();

    /** Render */
    return (
        <div className="language-selector">
            <div className="dropdown">
                <button
                    className="btn btn-link dropdown-toggle d-flex align-items-center text-decoration-none"
                    type="button"
                    onClick={toggleDropdown}
                    aria-expanded={isOpen}
                >
                    <img 
                        src={`${config.defaultPublicDirBaseUrl}img/flags/${currentLanguage.code}.svg`}
                        alt={currentLanguage.name}
                        className="flag-icon me-2"
                        width="20"
                        height="15"
                    />
                    <span className="d-none d-md-inline">{currentLanguage.code.toUpperCase()}</span>
                    <i className="fas fa-chevron-down ms-1"></i>
                </button>
                
                {isOpen && (
                    <>
                        <div className="dropdown-backdrop" onClick={closeDropdown}></div>
                        <ul className="dropdown-menu show">
                            {config.languages.map((language) => (
                                <li key={language.code}>
                                    <button
                                        className={`dropdown-item d-flex align-items-center ${currentLanguage.code === language.code ? 'active' : ''}`}
                                        onClick={() => handleLanguageChange(language.code)}
                                    >
                                        <img 
                                            src={`${config.defaultPublicDirBaseUrl}img/flags/${language.code}.svg`}
                                            alt={language.name}
                                            className="flag-icon me-2"
                                            width="20"
                                            height="15"
                                        />
                                        <span>{language.name}</span>
                                        {currentLanguage.code === language.code && (
                                            <i className="fas fa-check ms-auto text-primary"></i>
                                        )}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
        </div>
    );
}

export default LanguageSelector;